import React, { useEffect, useMemo, useState } from 'react';
import styles from './SearchBar.module.scss';
import { SearchQuery, SearchResultDto } from 'services/api/api-client';
import { IconButton } from '../../../../components/uikit/IconButton.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import CustomPage from 'assets/icons/custom-page.svg?react';
import InfoPage from 'assets/icons/info-page.svg?react';
import FileDownload from 'assets/icons/file-download.svg?react';
import Cross from 'assets/icons/close-button-small.svg?react';
import SearchIcon from 'assets/icons/search-blue.svg?react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { buildUrl, highlightMatches } from 'helpers/search-helper.tsx';
import { Links } from '../../../../application/constants/links.ts';
import Chevron from 'assets/icons/small_chevron.svg?react';
import { ActionText } from '../../../../components/uikit/ActionText.tsx';
import clsx from 'clsx';
import { Trans } from 'react-i18next';

const useDebounce = <T,>(value: T, delay = 500): T => {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

export const SearchBar = () => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isHoveredOverList, setIsHoveredOverList] = useState(false);
  const i18n = useScopedTranslation('SearchBar');
  const location = useLocation();

  useEffect(() => {
    setInputValue('');
  }, [location]);

  const debouncedInput = useDebounce(inputValue);

  const searchQuery = SearchQuery.useSearchQuery(
    {
      search: debouncedInput,
    },
    {
      enabled: !!debouncedInput,
      throwOnError: false,
    },
  );

  const options = useMemo(() => {
    if (searchQuery?.data?.data) {
      return searchQuery.data.data;
    }

    return [];
  }, [searchQuery.data]);

  const clearInput = () => {
    setInputValue('');
    setIsOpen(false);
    setIsHoveredOverList(false);
  };

  const getAdvancedSearchLink = () => {
    const option: SearchResultDto = {
      cmsPageUrl: '',
      fileLink: '',
      pageName: 'Advanced search',
      foundSearchValue: inputValue,
      matchedFields: {
        searchValue: inputValue,
      },
      matchedInnerFields: {},
    };
    return buildUrl(option);
  };

  const closeSearch = () => {
    setIsOpen(false);
    setIsHoveredOverList(false);
    clearInput();
  };

  return (
    <div className={styles.relative}>
      <SearchIcon className={styles.searchIcon} />
      <Input
        testId={'search-input'}
        onFocus={(e) => {
          setIsOpen(true);
        }}
        onBlur={(e) => {
          if (!isHoveredOverList) {
            setIsOpen(false);
          }
        }}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={i18n.t('placeholder')}
        className={styles.searchBarContainer}
        endAdornment={
          <div>
            {inputValue && (
              <IconButton
                iconClassName={styles.crossButton}
                type={'destroy'}
                onClick={clearInput}
              >
                <Cross />
              </IconButton>
            )}
          </div>
        }
        endAdornmentClassname={styles.cross}
      />
      {isOpen && !inputValue && (
        <div
          className={styles.optionContainer}
          onMouseOver={() => setIsHoveredOverList(true)}
          onMouseLeave={() => setIsHoveredOverList(false)}
        >
          <Link
            to={Links.Authorized.AdvancedSearch.route}
            className={styles.link}
            onClick={closeSearch}
          >
            <ActionText
              className={styles.goToAdvancedSearch}
              text={i18n.t('go_advanced_search')}
              Svg={Chevron}
              testId={'go-advanced-search-link'}
            />
          </Link>
        </div>
      )}
      {isOpen && inputValue && !options.length && searchQuery.isFetched && (
        <div
          className={styles.optionContainer}
          onMouseOver={() => setIsHoveredOverList(true)}
          onMouseLeave={() => setIsHoveredOverList(false)}
        >
          <div className={styles.notFound}>
            <Trans
              i18nKey={'SearchBar.not_found'}
              components={{ bold: <strong /> }}
            />
          </div>
          <Link
            to={getAdvancedSearchLink()}
            onClick={closeSearch}
            className={styles.link}
          >
            <ActionText
              className={styles.goToAdvancedSearch}
              text={i18n.t('go_advanced_search')}
              Svg={Chevron}
            />
          </Link>
        </div>
      )}
      {!!options.length && isOpen && inputValue && (
        <div
          className={styles.optionContainer}
          onMouseOver={() => setIsHoveredOverList(true)}
          onMouseLeave={() => setIsHoveredOverList(false)}
        >
          <div className={styles.overflow}>
            {options.map((option) => {
              const url = buildUrl(option);

              return (
                <Link
                  key={
                    option.pageName +
                    option.foundSearchValue +
                    (option.cmsPageUrl ?? '') +
                    (option.fileLink ?? '')
                  }
                  className={styles.option}
                  to={url}
                  onClick={closeSearch}
                >
                  <div>
                    {option.fileLink ? (
                      <FileDownload />
                    ) : option.cmsPageUrl ? (
                      <CustomPage />
                    ) : (
                      <InfoPage />
                    )}
                  </div>
                  <div className={styles.columnContainer}>
                    <div>
                      {highlightMatches(
                        option.foundSearchValue ?? option.pageName,
                        inputValue,
                        styles.highlight,
                      )}
                    </div>
                    <div className={styles.greyText}>
                      {highlightMatches(
                        option.pageName,
                        inputValue,
                        styles.highlight,
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className={styles.borderTop}>
            <Link
              to={getAdvancedSearchLink()}
              onClick={closeSearch}
              className={styles.link}
            >
              <ActionText
                className={clsx(styles.goToAdvancedSearch)}
                text={i18n.t('see_results')}
                Svg={Chevron}
              />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
